import { useState, type ReactNode } from "react"
import * as Validator from "yup"
import {
	Button,
	Divider,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	RadioInputField,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import {
	OTA_MEAL_CATEGORIES,
	TOtaMealCategory,
	TOtaMealPlanPayload,
} from "./store"
import {
	HotelConfigLockButton,
	type TUnlockHotelConfigCopy,
} from "../shared/HotelConfigLock"
import { emptyOtaMealPlanFormValues, otaMealPlanToFormValues } from "./utils"

export interface IOtaMealPlanFormSchema {
	name: string
	description: string
	// Radio inputs can't hold `null`, so "None" is `"none"` in the form.
	meal_category: TOtaMealCategory | "none"
}

const otaMealPlanValidationSchema = Validator.object().shape({
	name: Validator.string()
		.required("Name is required")
		.max(191, "Please use 191 or less characters"),
	description: Validator.string().max(255, "Please use 255 or less characters"),
	meal_category: Validator.mixed().oneOf([
		...OTA_MEAL_CATEGORIES.map((category) => category.value),
		"none",
	]),
})

function otaMealPlanFormValuesToPayload(
	values: IOtaMealPlanFormSchema
): TOtaMealPlanPayload {
	return {
		name: values.name.trim(),
		description: values.description.trim() || null,
		meal_category:
			values.meal_category === "none" ? null : values.meal_category,
	}
}

/**
 * Add/Edit meal plan form. It does not save anything itself; the caller
 * decides what to do with the values (call the api, keep them in a form).
 */
function OtaMealPlanForm({
	submitLabel,
	initialValues = emptyOtaMealPlanFormValues,
	notice,
	unlockCopy,
	onSubmit,
}: {
	submitLabel: string
	// only passed when editing
	initialValues?: IOtaMealPlanFormSchema
	// shown once the form is unlocked, or always when there is nothing to unlock
	notice?: ReactNode
	// only passed when editing, the form then starts with the meal category locked
	unlockCopy?: TUnlockHotelConfigCopy
	onSubmit: (payload: TOtaMealPlanPayload) => Promise<void> | void
}) {
	const [isLocked, setIsLocked] = useState(Boolean(unlockCopy))
	return (
		<Form<IOtaMealPlanFormSchema>
			initialValues={initialValues}
			validate={validateFormValues(otaMealPlanValidationSchema)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(otaMealPlanFormValuesToPayload(values))
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Stack gap="6" padding="6">
							{isLocked ? null : notice}
							<TextInputField
								name="name"
								label="Name"
								type="text"
								placeholder="Type"
							/>
							<TextAreaInputField
								name="description"
								label="Description"
								placeholder="Type"
								rows={3}
							/>
							<Stack gap="3">
								<Text fontWeight="semibold">Meal Category</Text>
								<Stack gap="3">
									{OTA_MEAL_CATEGORIES.map((category) => (
										<RadioInputField
											key={category.value}
											name="meal_category"
											value={category.value}
											label={category.value}
											secondaryLabel={category.meals}
											disabled={isLocked}
											hideErrors
										/>
									))}
									<RadioInputField
										name="meal_category"
										value="none"
										label="None"
										disabled={isLocked}
									/>
								</Stack>
							</Stack>
							<SubmissionError />
						</Stack>
						<Divider marginY="0" />
						<Inline
							gap="4"
							padding="4"
							paddingTop="0"
							alignItems="center"
							justifyContent={unlockCopy ? "between" : "end"}
						>
							{unlockCopy ? (
								<HotelConfigLockButton
									{...unlockCopy}
									label="meal plan"
									isLocked={isLocked}
									onUnlock={() => setIsLocked(false)}
									onLock={() => {
										form.change("meal_category", initialValues.meal_category)
										setIsLocked(true)
									}}
								/>
							) : null}
							<Button type="submit" level="primary" disabled={submitting}>
								{submitting ? <Icons.Spinner /> : null}{" "}
								{submitting ? "Saving..." : submitLabel}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

/**
 * Add/Edit meal plan in a dialog. Edits when `mealPlan` is passed, otherwise adds.
 * Like the form, it only hands the values over to `onSubmit`.
 */
export function OtaMealPlanInDialog({
	mealPlan,
	notice,
	unlockCopy,
	open,
	onClose,
	onSubmit,
}: {
	// only passed when editing
	mealPlan?: TOtaMealPlanPayload
	notice?: ReactNode
	// only passed when editing, see OtaMealPlanForm
	unlockCopy?: TUnlockHotelConfigCopy
	open: boolean
	onClose: () => void
	onSubmit: (payload: TOtaMealPlanPayload) => Promise<void> | void
}) {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			title={mealPlan ? "Edit Meal Plan" : "Add Meal Plan"}
			sm
		>
			<Dialog.Body paddingX="0" paddingY="0">
				<OtaMealPlanForm
					submitLabel={mealPlan ? "Update" : "Add Meal Plan"}
					initialValues={otaMealPlanToFormValues(mealPlan)}
					notice={notice}
					unlockCopy={unlockCopy}
					onSubmit={onSubmit}
				/>
			</Dialog.Body>
		</Dialog>
	)
}
